import React, { useState } from 'react';
import { Save, RotateCcw, Plus, Trash2, Video, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { PageHeader, Card, SimpleBadge } from './shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import VideoUpload from '../components/VideoUpload';
import { CoverageManager, DeliveryPricingSettings } from './CoverageManager';

const Row = ({ label, desc, children }) => (
  <div className="flex items-center justify-between gap-4 py-4 border-b border-gray-100 last:border-0">
    <div><p className="font-semibold text-sm">{label}</p>{desc && <p className="text-xs text-gray-500">{desc}</p>}</div>
    {children}
  </div>
);

const AdsManager = () => {
  const { ads, createAd, updateAd, deleteAd } = useApp();
  const [draft, setDraft] = useState({ title: '', videoUrl: '' });
  const create = () => {
    if (!draft.title.trim() || !draft.videoUrl) return toast.error('Sube el video y escribe un título');
    createAd(draft).then(() => { setDraft({ title: '', videoUrl: '' }); toast.success('Anuncio publicado'); });
  };
  return (
    <Card title="Videos publicitarios" className="xl:col-span-2">
      <div className="px-5 pb-5 space-y-4">
        <p className="text-sm text-gray-500 -mt-1">Se muestran a los clientes en la app. Máx. 20 MB, 30 segundos.</p>
        <div className="grid sm:grid-cols-[1fr_260px] gap-3 items-start bg-[#F7F8FA] rounded-xl p-4">
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Título del anuncio</Label><Input data-testid="ad-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Ej. Promo de fin de semana" /></div>
            <Button data-testid="ad-publish" className="bg-[#EE3B36] hover:bg-[#D93129] gap-2" onClick={create}><Plus size={16} /> Publicar anuncio</Button>
          </div>
          <VideoUpload testId="ad-video-upload" value={draft.videoUrl} onChange={(url) => setDraft({ ...draft, videoUrl: url })} />
        </div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {ads.map((a) => (
            <div key={a.id} data-testid={`ad-${a.id}`} className="rounded-xl border border-gray-200 overflow-hidden bg-white">
              <video src={a.videoUrl} className="w-full h-28 object-cover bg-black" muted />
              <div className="p-3 flex items-center justify-between gap-2">
                <div className="min-w-0"><p className="text-sm font-semibold truncate">{a.title}</p><SimpleBadge tone={a.active ? 'green' : 'gray'}>{a.active ? 'Activo' : 'Pausado'}</SimpleBadge></div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Switch data-testid={`ad-toggle-${a.id}`} checked={a.active} onCheckedChange={(v) => updateAd(a.id, { active: v })} />
                  <button data-testid={`ad-delete-${a.id}`} onClick={() => deleteAd(a.id).then(() => toast('Anuncio eliminado'))} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
          {ads.length === 0 && <p className="col-span-full text-center text-gray-400 py-6 text-sm flex items-center justify-center gap-2"><Video size={16} /> Sin anuncios publicados</p>}
        </div>
      </div>
    </Card>
  );
};

const AdminSettings = () => {
  const { resetDemo } = useApp();
  const [s, setS] = useState({ name: 'Delibery 7', phone: '+504 2444-7777', email: 'contacto@delivery7tocoa.com', city: 'Tocoa, Colón, Honduras', baseFee: 20, mandaditoFee: 10, perKm: 8, commission: 12, minOrder: 50, hours: '7:00 AM - 11:00 PM', notifyOrders: true, notifyPayments: true, notifyDrivers: true, autoAssign: false, cardPayments: false, message: '¡Gracias por confiar en Delibery 7! Tu ciudad, en tus manos.' });
  const set = (k) => (e) => setS({ ...s, [k]: e?.target ? e.target.value : e });
  return (
    <div>
      <PageHeader title="Configuración" subtitle="Ajustes generales del sistema" actions={<Button data-testid="settings-save" className="bg-[#EE3B36] hover:bg-[#D93129] gap-2" onClick={() => toast.success('Configuración guardada')}><Save size={16} /> Guardar cambios</Button>} />
      <div className="grid xl:grid-cols-2 gap-4">
        <Card title="Información de la empresa" className="p-0">
          <div className="px-5 pb-5 grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Nombre</Label><Input value={s.name} onChange={set('name')} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Teléfono</Label><Input value={s.phone} onChange={set('phone')} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Correo</Label><Input value={s.email} onChange={set('email')} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Ciudad</Label><Input value={s.city} onChange={set('city')} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Horario de operación</Label><Input value={s.hours} onChange={set('hours')} /></div>
            <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Mensaje de agradecimiento</Label><Textarea value={s.message} onChange={set('message')} rows={2} /></div>
          </div>
        </Card>
        <DeliveryPricingSettings />
        <Card title="Notificaciones y operación">
          <div className="px-5 pb-2">
            <Row label="Alertas de nuevos pedidos" desc="Notificar al administrador en tiempo real"><Switch checked={s.notifyOrders} onCheckedChange={set('notifyOrders')} /></Row>
            <Row label="Alertas de comprobantes" desc="Nuevos comprobantes de pago pendientes"><Switch checked={s.notifyPayments} onCheckedChange={set('notifyPayments')} /></Row>
            <Row label="Alertas de conductores" desc="Nuevos registros pendientes de aprobación"><Switch checked={s.notifyDrivers} onCheckedChange={set('notifyDrivers')} /></Row>
            <Row label="Asignación automática de conductores" desc="Asignar al conductor disponible más cercano"><Switch checked={s.autoAssign} onCheckedChange={set('autoAssign')} /></Row>
          </div>
        </Card>
        <Card title="Métodos de pago">
          <div className="px-5 pb-2">
            <Row label="Transferencia bancaria" desc="Bancos hondureños con comprobante"><Switch checked disabled /></Row>
            <Row label="Tarjeta de crédito / débito" desc="Próximamente - requiere pasarela de pago"><Switch checked={s.cardPayments} onCheckedChange={set('cardPayments')} /></Row>
            <Row label="Efectivo contra entrega" desc="No incluido en el alcance actual"><Switch disabled /></Row>
          </div>
        </Card>
        <AdsManager />
        <CoverageManager />
        <Card title="Datos de demostración" className="xl:col-span-2">
          <div className="px-5 pb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-sm text-gray-600">Restablece todos los datos de la demo (pedidos, restaurantes, conductores, carrito) a su estado inicial.</p>
            <Button variant="outline" className="gap-2 text-red-600 border-red-200 hover:bg-red-50" onClick={resetDemo} data-testid="reset-demo"><RotateCcw size={16} /> Restablecer datos</Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminSettings;
